import React, { useState } from 'react';
import Card from '../components/ui/Card';
import { useAppContext } from '../contexts/AppContext';
import { Shield, Search, Edit2, Save, X } from 'lucide-react';
import ActiveProjectBadge from '../components/ActiveProjectBadge';
import { normalizeProjectName, extractProjectName } from '../utils/validation';
import { ModuleInfoModal } from '../components/ModuleInfoModal';
import { BomItem } from '../types';

const ControlChip: React.FC<{ controlId: string, onChipClick: (id: string) => void }> = ({ controlId, onChipClick }) => {
    const { allData } = useAppContext();
    const control = (allData.controls || []).find(c => c.id === controlId);
    if (!control) return null;

    const isPrev = control.type === 'prevention';
    return (
        <button
            onClick={() => onChipClick(controlId)}
            title={control.name}
            className={`inline-flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded-full border cursor-pointer hover:scale-105 transition-transform ${isPrev
                ? 'bg-chip-prev-bg text-chip-prev border-chip-prev'
                : 'bg-chip-det-bg text-chip-det border-chip-det'
                }`}
        >
            {isPrev ? <Shield size={12} /> : <Search size={12} />}
            {control.id}
        </button>
    );
};

import { ModalWrapper } from '../components/ModalWrapper';
import { useProjectFilter } from '../hooks/useProjectFilter';

// Modal for editing the basic function of a BOM item
const EditFunctionModal: React.FC<{
    item: BomItem;
    onSave: (item: BomItem) => void;
    onClose: () => void;
}> = ({ item, onSave, onClose }) => {
    const [basicFunction, setBasicFunction] = useState(item.basicFunction || '');

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        onSave({ ...item, basicFunction });
    };

    return (
        <ModalWrapper
            isOpen={true}
            onClose={onClose}
            title={
                <>
                    <Edit2 size={18} className="text-primary" />
                    Edit Component Function
                </>
            }
            maxWidth="max-w-[500px]"
        >
            <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                    <p className="text-xs font-bold text-text-secondary uppercase mb-1">Component</p>
                    <p className="text-sm text-text-primary font-semibold">[{item.id}] {item.component}</p>
                </div>

                <div>
                    <label className="block text-xs font-bold text-text-secondary uppercase mb-1">Basic Function</label>
                    <textarea
                        value={basicFunction}
                        onChange={e => setBasicFunction(e.target.value)}
                        className="w-full p-3 rounded border border-border bg-card text-text-primary h-32 resize-none focus:ring-2 focus:ring-primary focus:border-transparent"
                        placeholder="Primary purpose of this component in the system..."
                        required
                        autoFocus
                    />
                    <p className="text-[10px] text-text-secondary mt-2 leading-tight">
                        Define what this component *does* in the system. High-quality function definitions
                        lead to better FMEA results and more effective controls.
                    </p>
                </div>

                <div className="flex justify-end gap-2 pt-4 border-t border-border">
                    <button
                        type="button"
                        onClick={onClose}
                        className="px-4 py-2 text-sm font-medium text-text-secondary hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg"
                    >
                        Cancel
                    </button>
                    <button
                        type="submit"
                        className="px-4 py-2 text-sm font-bold text-white bg-primary hover:bg-primary-dark rounded-lg flex items-center gap-2 shadow-lg"
                    >
                        <Save size={16} /> Save Changes
                    </button>
                </div>
            </form>
        </ModalWrapper>
    );
};

const BomFunctions: React.FC = () => {
    const { allData, highlightedItem, navigate, activeProject, updateBomItem } = useAppContext();
    const { bomItems, fmeaItems, controls, iterationHistory } = allData;
    const [editingItem, setEditingItem] = useState<BomItem | null>(null);
    const { isItemInProject } = useProjectFilter();

    const handleControlClick = (controlId: string) => {
        navigate('/graph-view', { highlight: { type: 'control', id: controlId } });
    };

    const filteredBomItems = (bomItems || []).filter(item => isItemInProject(item.projectName, item.epochId));

    const handleSave = async (updatedItem: BomItem) => {
        await updateBomItem(updatedItem);
        setEditingItem(null);
    };

    return (
        <div className="space-y-6">
            <div className="flex justify-between items-center">
                <div>
                    <h1 className="text-3xl font-bold text-text-primary">BOM Function List</h1>
                    <p className="text-text-secondary">Each BOM item&apos;s basic function, with related controls and FMEAs.</p>
                </div>
                <div className="flex items-center gap-3">
                    <ModuleInfoModal
                        title="BOM Function Mapping"
                        description={`The BOM Function List shows how each Bill of Materials component maps to its basic function within the system. This view provides complete traceability from components through controls to FMEA items${activeProject !== 'all' ? ' for your selected project' : ''}.`}
                        description2="Each BOM item can have multiple prevention and detection controls assigned to it. These controls are then referenced in FMEA items to mitigate failure modes, creating a complete chain of accountability from hardware to risk mitigation."
                        benefits={[
                            `See the basic function of each component in the ${activeProject !== 'all' ? activeProject : 'current'} project`,
                            "Identify which prevention (🛡️) and detection (🔍) controls apply to each component",
                            "Trace from BOM items through controls to specific FMEA failure mode entries",
                            "Ensure complete control coverage across all critical components",
                            "Support for design reviews and traceability audits",
                            "Clickable control chips jump to Graph View for relationship visualization"
                        ]}
                        usageInstructions={[
                            "Review each component's basic function to understand its role in the system",
                            "Click prevention (orange) or detection (blue) control chips to see their full details",
                            "Click FMEA links to jump directly to related failure mode entries",
                            "Use this view to verify all critical components have appropriate controls",
                            "Export or screenshot this table for design review documentation",
                            "Check that BOM items without controls are truly low-risk or non-functional"
                        ]}
                    />
                    <ActiveProjectBadge showStandard={false} />
                </div>
            </div>

            <Card>
                <div className="overflow-x-auto">
                    <table className="w-full text-sm text-left text-text-secondary">
                        <thead className="text-xs text-text-primary uppercase bg-background/50">
                            <tr>
                                <th scope="col" className="px-6 py-3">Component</th>
                                <th scope="col" className="px-6 py-3">Basic Function</th>
                                <th scope="col" className="px-6 py-3">Related Controls</th>
                                <th scope="col" className="px-6 py-3">Related FMEAs</th>
                                <th scope="col" className="px-6 py-3 w-16">Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {filteredBomItems.map((bomItem) => {
                                const relatedControls = (controls || []).filter(c => (c.bomIds || []).includes(bomItem.id));
                                const relatedControlIds = relatedControls.map(c => c.id);
                                const relatedFmeas = (fmeaItems || []).filter(f =>
                                    (f.preventionControls || []).some((cId: string) => relatedControlIds.includes(cId)) ||
                                    (f.detectionControls || []).some((cId: string) => relatedControlIds.includes(cId))
                                );

                                const isHighlighted = highlightedItem?.type === 'bom' && highlightedItem.id === bomItem.id;

                                return (
                                    <tr key={bomItem.id} className={`border-b border-border hover:bg-background/50 ${isHighlighted ? 'animate-highlight bg-primary/5' : ''}`}>
                                        <td className="px-6 py-4 font-medium text-text-primary">
                                            <div className="flex flex-col">
                                                <span className="text-[10px] text-text-secondary font-bold">[{bomItem.id}]</span>
                                                <span>{bomItem.component}</span>
                                            </div>
                                        </td>
                                        <td className="px-6 py-4 leading-relaxed max-w-xs">{bomItem.basicFunction}</td>
                                        <td className="px-6 py-4">
                                            <div className="flex flex-wrap gap-2">
                                                {relatedControls.map(c => (
                                                    <ControlChip key={c.id} controlId={c.id} onChipClick={handleControlClick} />
                                                ))}
                                                {relatedControls.length === 0 && <span className="text-[10px] text-text-secondary italic">No controls</span>}
                                            </div>
                                        </td>
                                        <td className="px-6 py-4">
                                            <div className="flex flex-col gap-1">
                                                {relatedFmeas.map(fmea => (
                                                    <button key={fmea.id} onClick={() => navigate('/fmeas', { highlight: { type: 'fmea', id: fmea.id } })} className="text-left text-primary hover:underline text-[10px] truncate max-w-[150px]">
                                                        {fmea.id}: {fmea.failureMode}
                                                    </button>
                                                ))}
                                                {relatedFmeas.length === 0 && <span className="text-[10px] text-text-secondary italic">No FMEAs</span>}
                                            </div>
                                        </td>
                                        <td className="px-6 py-4 text-center">
                                            <button
                                                onClick={() => setEditingItem(bomItem)}
                                                className="p-1.5 text-primary hover:bg-primary/10 rounded-full transition-all hover:scale-110 active:scale-95"
                                                title="Edit Function"
                                            >
                                                <Edit2 size={16} />
                                            </button>
                                        </td>
                                    </tr>
                                );
                            })}
                            {filteredBomItems.length === 0 && (
                                <tr>
                                    <td colSpan={5} className="px-6 py-12 text-center text-text-secondary italic">
                                        No components found for this project filter.
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </Card>

            {editingItem && (
                <EditFunctionModal
                    item={editingItem}
                    onSave={handleSave}
                    onClose={() => setEditingItem(null)}
                />
            )}
        </div>
    );
};

export default BomFunctions;
